/** Fields sent to `create_initiative` when the user commits a draft. */
export type InitiativeFields = {
    title: string;
    description: string;
    lever: string;
    priority: string;
    start_date: string;
};

export type CommitResult = {
    id: number;
    url: string;
    duplicate: boolean;
};

export type CommittedDraft = {
    /** Identity of the proposal that was committed; a marker for a different proposal is ignored. */
    draftKey: string;
    fields: InitiativeFields;
    result: CommitResult;
};

const COMMITTED_PREFIX = "initiative-draft-committed:";
const DISCARDED_PREFIX = "initiative-draft-discarded:";

function isString(value: unknown): value is string {
    return typeof value === "string";
}

function parseCommitted(raw: string, draftKey: string): CommittedDraft | null {
    const value = JSON.parse(raw) as {
        draftKey?: unknown;
        fields?: Record<string, unknown>;
        result?: Record<string, unknown>;
    } | null;
    const fields = value?.fields;
    const result = value?.result;
    if (value?.draftKey !== draftKey || fields == null || result == null) return null;
    if (![fields.title, fields.description, fields.lever, fields.priority, fields.start_date].every(isString)) {
        return null;
    }
    if (typeof result.id !== "number" || !isString(result.url) || typeof result.duplicate !== "boolean") {
        return null;
    }
    return value as CommittedDraft;
}

/** Commit markers use localStorage so another tab in the same browser also sees the draft as created. */
export function readCommitted(messageId: string, draftKey: string): CommittedDraft | null {
    try {
        const raw = localStorage.getItem(COMMITTED_PREFIX + messageId);
        return raw ? parseCommitted(raw, draftKey) : null;
    } catch {
        return null;
    }
}

export function writeCommitted(messageId: string, committed: CommittedDraft): void {
    try {
        localStorage.setItem(COMMITTED_PREFIX + messageId, JSON.stringify(committed));
    } catch {
        // Storage unavailable: the card still works, it just forgets on reload.
    }
}

export function readDiscarded(messageId: string): boolean {
    try {
        return sessionStorage.getItem(DISCARDED_PREFIX + messageId) === "1";
    } catch {
        return false;
    }
}

export function writeDiscarded(messageId: string, discarded: boolean): void {
    try {
        if (discarded) sessionStorage.setItem(DISCARDED_PREFIX + messageId, "1");
        else sessionStorage.removeItem(DISCARDED_PREFIX + messageId);
    } catch {
        // See writeCommitted.
    }
}
